import { ComponentConfig } from '@measured/puck';
import { v4 as uuidv4 } from 'uuid';
import {
  MarginProps,
  marginFields,
  marginProps,
} from '../../../page-builder/puck/reusable-props/NewMargin';
import { PaddingProps, paddingFields } from '../../puck/reusable-props/padding';
import {
  ImageProps,
  imageProps,
} from '../../../page-builder/puck/reusable-props/image-props';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  defaultImage,
  ImageSelector,
} from '../../../components/common/image-selector/ImageSelector';
import Border, {
  IBorderValueProps,
  borderProps,
} from '../../../page-builder/puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import InputField from '../../../components/common/input-field';
import CustomTooltip from '../../../components/common/tooltip';
import Button from '../../../components/common/button';
import { FieldLabel } from '@measured/puck';
import {
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  getBorderProps,
  getNumberPattern,
} from '../../utils/helper';
import { IAsset } from '../../interface/apiServiceInterface';
import { CustomerProps } from '../carousel review';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../puck/reusable-props/widthAndHeight';
import WithAccordion from '../customComponents/CustomAccordion';
import { toolTipInfo } from '../../utils/constants';
import { ToggleButtonGroup, ToggleButton, Switch } from '@mui/material';
import { CustomCarousel } from './CustomCarousel';
import { Select } from '../../components/form-fields/Select';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import BackgroungColorComponent from '../customComponents/BackgroungColorComponent';

export interface CarouselData {
  id: string;
  image: IAsset;
  thumbnailImage: IAsset;
  imageSize: WidthAndHeightSubProps;
}

export interface CarouselImageProps
  extends MarginProps,
    PaddingProps,
    showComponentProps,
    ImageProps {
  carousels: CarouselData[];
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  isGlobalSize: boolean;
  customization: {
    interval: number;
    autoScroll: boolean;
    hideArrows: boolean;
    hideThumbnails: boolean;
    transition: string;
    thumbnailSize: WidthAndHeightSubProps;
    thumbnailGap: string;
    arrowColor: string;
    arrowBackground: {
      color: string;
      isInherit: boolean;
      hoverColor?: string;
    };
    lazyLoading: boolean;
  };
}

const transitionOptions = [
  { label: '100ms', value: 'duration-100' },
  { label: '150ms', value: 'duration-150' },
  { label: '200ms', value: 'duration-200' },
  { label: '300ms', value: 'duration-300' },
  { label: '500ms', value: 'duration-500' },
  { label: '700ms', value: 'duration-700' },
  { label: '1000ms', value: 'duration-1000' },
];

export const CarouselImage = ({
  baseService,
  useAppSelector,
}: CustomerProps): ComponentConfig<CarouselImageProps> => {
  return {
    label: (
      <CustomTooltip title="Carousel Image">
        <span>Image Carousel</span>
      </CustomTooltip>
    ) as unknown as string,

    fields: {
      ...showComponent,
      carousels: {
        type: 'custom',
        label: 'Carousel Image',
        render: ({ value, onChange }) => {
          value = value || [];

          const handleAdd = () => {
            const newTestimonial: CarouselData = {
              id: uuidv4(),
              image: defaultImage,
              thumbnailImage: defaultImage,
              imageSize: {
                height: {
                  desktop: '100%',
                  mobile: '100%',
                  tablet: '100%',
                },
                maxWidth: {
                  desktop: '100%',
                  mobile: '100%',
                  tablet: '100%',
                },
                deviceType: 'desktop',
              },
            };
            onChange([newTestimonial, ...value]);
          };

          const handleEdit = (
            index: number,
            key: keyof CarouselData,
            newValue: string | IAsset | WidthAndHeightSubProps,
          ) => {
            onChange(
              value.map((item, i) =>
                i === index ? { ...item, [key]: newValue } : item,
              ),
            );
          };

          const handleRemove = (index: number) => {
            onChange(value.filter((_, i) => i !== index));
          };

          return (
            <WithAccordion title="Images">
              <div>
                <Button
                  label="+ Add Image"
                  onClick={handleAdd}
                  className="w-full mt-4"
                  variant="phoenix"
                />
                {value.map((item: CarouselData, index: number) => (
                  <div key={item.id} className="mt-4 space-y-2">
                    <ImageSelector
                      selectedImage={item.image}
                      baseService={baseService}
                      handleSubmit={img => handleEdit(index, 'image', img)}
                    />
                    <div className="grid gap-2">
                      <label>Thumbnail Image</label>
                      <ImageSelector
                        selectedImage={item?.thumbnailImage}
                        baseService={baseService}
                        handleSubmit={img =>
                          handleEdit(index, 'thumbnailImage', img)
                        }
                      />
                    </div>
                    <div className="my-2">
                      <WidthAndHeight
                        value={item.imageSize}
                        onChange={val => {
                          const updatedCarousels = value.map(
                            (carousel: CarouselData, i: number) =>
                              i === index
                                ? { ...carousel, imageSize: val }
                                : carousel,
                          );
                          onChange(updatedCarousels);
                        }}
                        title={'Image Size Control'}
                      />
                    </div>
                    <Button
                      label="- Remove"
                      onClick={() => handleRemove(index)}
                      variant="phoenix_secondary"
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </WithAccordion>
          );
        },
      },
      ...imageProps([]),
      customization: {
        type: 'custom',
        label: 'Customization',
        render: ({ value, onChange }) => (
          <WithAccordion title="Customization">
            <FieldLabel label="Interval">
              <InputField
                value={value?.interval}
                onChange={e => {
                  const val = getNumberPattern(e);
                  if (val !== null) onChange({ ...value, interval: +val });
                }}
              />
            </FieldLabel>
            <div>
              <FieldLabel
                label={
                  (
                    <CustomTooltip
                      title={'Auto Scroll' as keyof typeof toolTipInfo}>
                      <span>Auto Scroll</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div"
                className="w-full">
                <div className="w-full p-2">
                  <ToggleButtonGroup
                    value={value?.autoScroll}
                    exclusive
                    className="w-full"
                    onChange={(_e, selectedValue) => {
                      if (selectedValue !== null) {
                        onChange({ ...value, autoScroll: selectedValue });
                      }
                    }}>
                    {[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ].map((option, index) => (
                      <ToggleButton
                        key={index}
                        size="small"
                        value={option.value}
                        disableRipple
                        aria-label={option.label}
                        className="flex-1">
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </div>
              </FieldLabel>
            </div>
            <div>
              <FieldLabel
                label={
                  (
                    <CustomTooltip
                      title={'Hide Arrows' as keyof typeof toolTipInfo}>
                      <span>Hide Arrows</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div"
                className="w-full">
                <div className="w-full p-2">
                  <ToggleButtonGroup
                    value={value?.hideArrows}
                    exclusive
                    className="w-full"
                    onChange={(_e, selectedValue) => {
                      if (selectedValue !== null) {
                        onChange({ ...value, hideArrows: selectedValue });
                      }
                    }}>
                    {[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ].map((option, index) => (
                      <ToggleButton
                        key={index}
                        size="small"
                        value={option.value}
                        disableRipple
                        aria-label={option.label}
                        className="flex-1">
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </div>
              </FieldLabel>
            </div>
            <div>
              <FieldLabel
                label={
                  (
                    <CustomTooltip
                      title={'Hide Thumbnails' as keyof typeof toolTipInfo}>
                      <span>Hide Thumbnails</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div"
                className="w-full">
                <div className="w-full p-2">
                  <ToggleButtonGroup
                    value={value?.hideThumbnails}
                    exclusive
                    className="w-full"
                    onChange={(_e, selectedValue) => {
                      if (selectedValue !== null) {
                        onChange({ ...value, hideThumbnails: selectedValue });
                      }
                    }}>
                    {[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ].map((option, index) => (
                      <ToggleButton
                        key={index}
                        size="small"
                        value={option.value}
                        disableRipple
                        aria-label={option.label}
                        className="flex-1">
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </div>
              </FieldLabel>
            </div>
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Transition Time">
                    <span>Transition Time</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <Select
                className="pagebuilder-theme rounded-md"
                value={value?.transition}
                onChange={e =>
                  onChange({
                    ...value,
                    transition: e.target.value,
                  })
                }
                options={transitionOptions}
              />
            </FieldLabel>
            <div />
            <div className="my-2">
              <WidthAndHeight
                value={value?.thumbnailSize}
                onChange={val => {
                  onChange({
                    ...value,
                    thumbnailSize: val,
                  });
                }}
                title={'Thumbnail Size Control'}
              />
            </div>
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Thumbnail Gap">
                    <span>Thumbnail Gap</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div">
              <InputField
                value={value?.thumbnailGap}
                onChange={e => {
                  let newValue = e.replace(/[^0-9%]/g, '');

                  // Ensure that only one '%' symbol remains at the end
                  if (newValue?.includes('%')) {
                    newValue =
                      newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                  }

                  // If the string is empty or contains only whitespace, set to 'auto'
                  if (!newValue.trim()) {
                    newValue = 'auto';
                  }

                  // If the value includes both digits and '%', clean up any non-digit/% characters
                  if (/\d/.test(newValue) && /%/.test(newValue)) {
                    newValue = newValue.replace(/[^0-9%]/g, '');
                  }

                  // Default to 'auto' if the value is still empty
                  if (!newValue.trim()) {
                    newValue = 'auto';
                  }

                  onChange({ ...value, thumbnailGap: newValue });
                }}
              />
            </FieldLabel>
            <ColorPicker
              value={value?.arrowColor}
              onChange={color =>
                onChange({
                  ...value,
                  arrowColor: color,
                })
              }
              tooltip="arrowColor"
              label="Arrow Color"
            />
            <BackgroungColorComponent
              value={value?.arrowBackground}
              onChange={arrowBackground =>
                onChange({
                  ...value,
                  arrowBackground,
                })
              }
              isAddHover={true}
              title="Arrow Background Color"
              label="Arrow Background Color"
            />
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Lazy Loading">
                    <span>Lazy Loading</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div"
              className="w-full">
              <div className="w-full p-2">
                <ToggleButtonGroup
                  value={value?.lazyLoading}
                  exclusive
                  className="w-full"
                  onChange={(_e, selectedValue) => {
                    if (selectedValue !== null) {
                      onChange({ ...value, lazyLoading: selectedValue });
                    }
                  }}>
                  {[
                    { label: 'On', value: true },
                    { label: 'Off', value: false },
                  ].map((option, index) => (
                    <ToggleButton
                      key={index}
                      size="small"
                      value={option.value}
                      disableRipple
                      aria-label={option.label}
                      className="flex-1">
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
            </FieldLabel>
          </WithAccordion>
        ),
      },
      isGlobalSize: {
        type: 'custom',
        label: 'Global Size',
        render: ({ value, onChange }) => (
          <div className="flex gap-1 items-center justify-between">
            <span>Use Global Size Control</span>
            <Switch
              checked={value}
              onChange={e => {
                onChange(e.target.checked);
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </div>
        ),
      },
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
    },

    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      carousels: [],
      isGlobalSize: true,
      customization: {
        interval: 5,
        autoScroll: true,
        hideArrows: false,
        hideThumbnails: false,
        transition: 'duration-700',
        thumbnailSize: {
          height: {
            desktop: '64',
            mobile: '64',
            tablet: '64',
          },
          maxWidth: {
            desktop: '64',
            mobile: '64',
            tablet: '64',
          },
          deviceType: 'desktop',
        },
        thumbnailGap: '8',
        arrowColor: '#ffffff',
        arrowBackground: {
          color: '#000000',
          isInherit: false,
          hoverColor: '#000000',
        },
        lazyLoading: false,
      },
      margin: marginProps.margin,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '10',
          mobile: '8',
          tablet: '10',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '10',
          mobile: '8',
          tablet: '10',
        },
      },
      image: {
        height: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
        maxWidth: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
        deviceType: 'desktop',
      },
      background: { color: '#ffffff', isInherit: false },
      ...borderProps,
    },

    render: ({
      carousels,
      customization,
      margin,
      padding,
      background,
      border,
      showComponent,
      image,
      editMode,
      isGlobalSize,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const device = getcurrentDeviceType(windowWidth, editMode);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);

      return (
        <div
          className={`w-full text-editor-content ${
            showComponent?.includes(device) ? '' : 'hidden'
          }`}
          style={{
            ...getNewMarginStyles(margin, device),
            ...getPaddingStyles(padding, device),
            ...getBorderProps(border, device),
            backgroundColor: background.isInherit
              ? 'inherit'
              : background.color,
          }}>
          <CustomCarousel
            carousels={carousels}
            interval={customization.interval}
            autoScroll={customization.autoScroll}
            hideArrows={customization.hideArrows}
            hideThumbnails={customization.hideThumbnails}
            transition={customization.transition}
            thumbnailSize={customization.thumbnailSize}
            thumbnailGap={customization.thumbnailGap}
            arrowColor={customization.arrowColor}
            isGlobalSize={isGlobalSize}
            arrowBackground={customization.arrowBackground}
            customImage={image}
            currentDeviceType={currentDeviceType}
            lazyLoading={customization.lazyLoading}
            editMode={editMode}
          />
        </div>
      );
    },
  };
};
